import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import type { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS, type Block, type Inline } from '@contentful/rich-text-types';
import type { Document } from '@contentful/rich-text-types';
import Markdown from 'react-markdown';
import { getJobBySlug } from '@/lib/contentful';
import type { JobPosting } from '@/types/job';

function isRichTextDocument(value: unknown): value is Document {
  return typeof value === 'object' && value !== null && 'nodeType' in value;
}

function RichContent({ content, fallback }: { content: Document | string | undefined; fallback: string }) {
  if (!content) {
    return <p className="font-body text-muted">{fallback}</p>;
  }

  if (isRichTextDocument(content)) {
    return <>{documentToReactComponents(content, richTextOptions)}</>;
  }

  if (typeof content === 'string' && content.trim().length > 0) {
    return (
      <Markdown
        components={{
          p: ({ children }) => (
            <p className="mb-4 font-body text-body leading-relaxed">{children}</p>
          ),
          h1: ({ children }) => (
            <h1 className="mb-4 mt-8 font-heading text-2xl font-semibold text-heading">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="mb-3 mt-7 font-heading text-xl font-semibold text-heading">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="mb-3 mt-6 font-heading text-lg font-semibold text-heading">{children}</h3>
          ),
          h4: ({ children }) => (
            <h4 className="mb-2 mt-4 font-heading text-base font-semibold text-heading">{children}</h4>
          ),
          h5: ({ children }) => (
            <h5 className="mb-2 mt-4 font-heading text-sm font-semibold text-heading">{children}</h5>
          ),
          h6: ({ children }) => (
            <h6 className="mb-2 mt-4 font-heading text-sm font-medium text-heading">{children}</h6>
          ),
          ul: ({ children }) => (
            <ul className="mb-4 ml-6 list-disc space-y-1 text-body">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="mb-4 ml-6 list-decimal space-y-1 text-body">{children}</ol>
          ),
          li: ({ children }) => (
            <li className="font-body leading-relaxed">{children}</li>
          ),
          strong: ({ children }) => (
            <strong className="font-semibold text-heading">{children}</strong>
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-brand-purple underline hover:text-brand-purple-dark"
            >
              {children}
            </a>
          ),
        }}
      >
        {content}
      </Markdown>
    );
  }

  return <p className="font-body text-muted">{fallback}</p>;
}

const richTextOptions: Options = {
  renderMark: {
    [MARKS.BOLD]: (text: React.ReactNode) => (
      <strong className="font-semibold text-heading">{text}</strong>
    ),
  },
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_node: Block | Inline, children: React.ReactNode) => (
      <p className="mb-4 font-body text-body leading-relaxed">{children}</p>
    ),
    [BLOCKS.HEADING_1]: (_node: Block | Inline, children: React.ReactNode) => (
      <h1 className="mb-4 mt-8 font-heading text-2xl font-semibold text-heading">
        {children}
      </h1>
    ),
    [BLOCKS.HEADING_2]: (_node: Block | Inline, children: React.ReactNode) => (
      <h2 className="mb-3 mt-7 font-heading text-xl font-semibold text-heading">
        {children}
      </h2>
    ),
    [BLOCKS.HEADING_3]: (_node: Block | Inline, children: React.ReactNode) => (
      <h3 className="mb-3 mt-6 font-heading text-lg font-semibold text-heading">
        {children}
      </h3>
    ),
    [BLOCKS.HEADING_4]: (_node: Block | Inline, children: React.ReactNode) => (
      <h4 className="mb-2 mt-4 font-heading text-base font-semibold text-heading">
        {children}
      </h4>
    ),
    [BLOCKS.HEADING_5]: (_node: Block | Inline, children: React.ReactNode) => (
      <h5 className="mb-2 mt-4 font-heading text-sm font-semibold text-heading">
        {children}
      </h5>
    ),
    [BLOCKS.HEADING_6]: (_node: Block | Inline, children: React.ReactNode) => (
      <h6 className="mb-2 mt-4 font-heading text-sm font-medium text-heading">
        {children}
      </h6>
    ),
    [BLOCKS.UL_LIST]: (_node: Block | Inline, children: React.ReactNode) => (
      <ul className="mb-4 ml-6 list-disc space-y-1 text-body">{children}</ul>
    ),
    [BLOCKS.OL_LIST]: (_node: Block | Inline, children: React.ReactNode) => (
      <ol className="mb-4 ml-6 list-decimal space-y-1 text-body">{children}</ol>
    ),
    [BLOCKS.LIST_ITEM]: (_node: Block | Inline, children: React.ReactNode) => (
      <li className="font-body leading-relaxed">{children}</li>
    ),
    [INLINES.HYPERLINK]: (node: Block | Inline, children: React.ReactNode) => (
      <a
        href={(node.data as { uri: string }).uri}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-brand-purple underline hover:text-brand-purple-dark"
      >
        {children}
      </a>
    ),
  },
};

function JobDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<JobPosting | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchJob() {
      if (!slug) return;
      setLoading(true);
      const data = await getJobBySlug(slug);
      setJob(data);
      setLoading(false);
    }
    fetchJob();
  }, [slug]);

  if (loading) {
    return (
      <div className="bg-brand-cream">
        <div className="mx-auto max-w-content px-6 py-16">
          <div className="animate-pulse space-y-6">
            <div className="h-4 w-24 rounded bg-gray-200" />
            <div className="h-10 w-96 rounded bg-gray-200" />
            <div className="flex gap-4">
              <div className="h-5 w-24 rounded bg-gray-100" />
              <div className="h-5 w-28 rounded bg-gray-100" />
              <div className="h-5 w-20 rounded bg-gray-100" />
            </div>
            <div className="h-px bg-gray-200" />
            <div className="space-y-3">
              <div className="h-4 w-full rounded bg-gray-100" />
              <div className="h-4 w-5/6 rounded bg-gray-100" />
              <div className="h-4 w-4/6 rounded bg-gray-100" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="bg-brand-cream">
        <div className="mx-auto max-w-content px-6 py-16 text-center">
          <h1 className="font-heading text-2xl font-semibold text-heading">
            Position Not Found
          </h1>
          <p className="mt-3 font-body text-body">
            This job posting may have been removed or the link may be incorrect.
          </p>
          <button onClick={() => navigate('/')} className="btn-primary mt-6">
            View All Positions
          </button>
        </div>
      </div>
    );
  }

  const formattedDate = new Date(job.postedDate).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <>
      <Helmet>
        <title>{job.title} | ISHI Health Careers</title>
        <meta
          name="description"
          content={`Apply for ${job.title} at ISHI Health. ${job.department} · ${job.location} · ${job.type}`}
        />
      </Helmet>

      {/* Header */}
      <section className="bg-brand-purple-dark">
        <div className="mx-auto max-w-content px-6 py-12 md:py-16">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 font-body text-sm text-white/60 transition-colors hover:text-white"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            All Positions
          </Link>

          <h1 className="mt-4 font-heading text-3xl font-semibold text-white md:text-4xl lg:text-5xl">
            {job.title}
          </h1>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <span className="flex items-center gap-1.5 font-body text-sm text-white/70">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              {job.department}
            </span>
            <span className="flex items-center gap-1.5 font-body text-sm text-white/70">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              {job.location}
            </span>
            <span className="rounded-full bg-white/10 px-3 py-1 font-body text-xs font-medium text-white">
              {job.type}
            </span>
            {job.salaryRange && (
              <span className="flex items-center gap-1.5 font-body text-sm text-white/70">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {job.salaryRange}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="bg-brand-cream">
        <div className="mx-auto max-w-content px-6 py-12 md:py-16">
          <div className="grid gap-12 lg:grid-cols-3">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <div className="rounded-xl border border-gray-100 bg-white p-8">
                <div className="[&>:last-child]:mb-0">
                  <RichContent content={job.description} fallback="No description provided." />
                </div>

                <div className="mt-8 border-t border-gray-100 pt-8">
                  <div className="[&>:last-child]:mb-0">
                    <RichContent content={job.requirements} fallback="No requirements listed." />
                  </div>
                </div>

                {/* Join Us – shown on every job posting */}
                <div className="mt-8 border-t border-gray-100 pt-8">
                  <h2 className="mb-6 font-heading text-xl font-semibold text-heading">
                    Join Us
                  </h2>
                  <div>
                    <p className="font-body text-body leading-relaxed">
                      We believe patients with heart failure and cardiometabolic disease deserve
                      proactive, well-coordinated care—delivered by teams supported with the
                      right systems and processes. If you're excited to build and scale clinical
                      operations that make high-quality care possible, we'd love to hear from you.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-8 space-y-6">
                <div className="rounded-xl border border-gray-100 bg-white p-6">
                  <h3 className="mb-4 font-heading text-lg font-semibold text-heading">
                    Quick Info
                  </h3>
                  <dl className="space-y-4">
                    <div>
                      <dt className="font-body text-xs font-medium uppercase tracking-wider text-muted">
                        Department
                      </dt>
                      <dd className="mt-1 font-body text-sm text-heading">{job.department}</dd>
                    </div>
                    <div>
                      <dt className="font-body text-xs font-medium uppercase tracking-wider text-muted">
                        Location
                      </dt>
                      <dd className="mt-1 font-body text-sm text-heading">{job.location}</dd>
                    </div>
                    <div>
                      <dt className="font-body text-xs font-medium uppercase tracking-wider text-muted">
                        Employment Type
                      </dt>
                      <dd className="mt-1 font-body text-sm text-heading">{job.type}</dd>
                    </div>
                    {job.salaryRange && (
                      <div>
                        <dt className="font-body text-xs font-medium uppercase tracking-wider text-muted">
                          Salary Range
                        </dt>
                        <dd className="mt-1 font-body text-sm text-heading">
                          {job.salaryRange}
                        </dd>
                      </div>
                    )}
                    <div>
                      <dt className="font-body text-xs font-medium uppercase tracking-wider text-muted">
                        Posted
                      </dt>
                      <dd className="mt-1 font-body text-sm text-heading">{formattedDate}</dd>
                    </div>
                  </dl>
                </div>

                <Link to={`/jobs/${job.slug}/apply`} className="btn-primary block text-center">
                  Apply Now
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

export default JobDetailPage;
